import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './StudentHome.css';

// ================================================================
// Imports
// ================================================================
import { useAuth } from '../context/AuthContext';
import { COURSE_DETAILS } from './CourseDetail';
import { useExams } from '../components/Exams/ExamContext.jsx';
import { useCoins } from '../context/CoinContext.jsx';
import { normalizeGradeId, SUBJECT_LABELS } from '../core/education/curriculum.js';
import { engine } from '../core/books/storage.js';
import dataEngine from '../core/books/data.js';
import { getToken } from '../utils/secureStorage';
import { getDailyPlan } from './aiCoachEngine';

// ================================================================
// Helpers
// ================================================================
const toPersianDigits = (n) =>
  String(n ?? '').replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const getTodayPersianDayIndex = () => (new Date().getDay() + 1) % 7;

const normalizeGrade = (grade) => normalizeGradeId(grade);

const SUBJECT_COLORS = {
  riazi: '#4f46e5',
  olum: '#22c55e',
  ghoran: '#14b8a6',
  farsi: '#ec4899',
  hedye: '#f59e0b',
  motaleat: '#f97316',
  tafakor: '#6366f1',
  kar: '#0891b2',
};

const SUBJECT_EMOJI = {
  riazi: '📐',
  olum: '🔬',
  ghoran: '🕌',
  farsi: '📖',
  hedye: '🎁',
  motaleat: '🌍',
  tafakor: '🧠',
  kar: '🛠️',
};

const DRAGONS = [
  { id: 200, name: 'تخم اژدها',             emoji: '🥚', image: '',              minScore: 0,     c1: '#fbbf24', c2: '#d97706', glow: 'rgba(251,191,36,0.65)' },
  { id: 201, name: 'تازه کار',       emoji: '🦎', image: '/medals/1.png', minScore: 10,    c1: '#24fbd7', c2: '#06cbd9', glow: 'rgba(36, 244, 251, 0.65)' },
  { id: 202, name: 'ماجراجو',     emoji: '🦖', image: '/medals/2.png', minScore: 100,   c1: '#84cc16', c2: '#65a30d', glow: 'rgba(132,204,22,0.65)' },
  { id: 203, name: 'حرفه ای',     emoji: '🐲', image: '/medals/3.png', minScore: 500,   c1: '#22d3ee', c2: '#0891b2', glow: 'rgba(34,211,238,0.65)' },
  { id: 204, name: 'قهرمان',      emoji: '🐦‍🔥', image: '/medals/4.png', minScore: 1000,  c1: '#f97316', c2: '#dc2626', glow: 'rgba(249,115,22,0.65)' },
  { id: 205, name: 'افسانه',       emoji: '🐉', image: '/medals/5.png', minScore: 2000,  c1: '#818cf8', c2: '#6366f1', glow: 'rgba(129,140,248,0.65)' },
  { id: 206, name: 'استاد',         emoji: '🔱', image: '/medals/6.png', minScore: 3500,  c1: '#baeafd', c2: '#0284c7', glow: 'rgba(145, 196, 255, 0.75)' },
  { id: 207, name: 'استاد بزرگ',    emoji: '🔥', image: '/medals/7.png', minScore: 6000,  c1: '#fde047', c2: '#eab308', glow: 'rgba(253,224,71,0.65)' },
  { id: 208, name: 'اسطوره',        emoji: '👑', image: '/medals/8.png', minScore: 10000, c1: '#e879f9', c2: '#a855f7', glow: 'rgba(232,121,249,0.65)' },
];

const getLeagueIdxByScore = (xp) => {
  let i = 0;
  DRAGONS.forEach((d, k) => { if (xp >= d.minScore) i = k; });
  return i;
};

const getDragonForXp = (xp) => DRAGONS[getLeagueIdxByScore(xp || 0)];















// ================================================================
// Weekly Progress — helpers, hook, chart (جایگزین Parent Card)
// ================================================================

const NICE_STEPS = [
  5, 10, 15, 20, 30, 40, 50, 75, 100,
  150, 200, 300, 500, 750, 1000,
  2000, 3000, 5000, 10000,
];
const niceMax = (v) => {
  if (v <= 0) return 5;
  const found = NICE_STEPS.find((s) => s >= v);
  return found || Math.ceil(v / 5000) * 5000;
};
const ymdLocal = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const startOfDayLocal = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDaysLocal = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};





// ================================================================
// Hooks — data layer (100% preserved from old version)
// ================================================================

// ---- useTodayPlan ----
const useTodayPlan = (userId) => {
  const [todayPlan, setTodayPlan] = useState({
    hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0,
  });
  const [tick, setTick] = useState(0);

  const readPlan = useCallback(() => {
    if (!userId) {
      setTodayPlan({ hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0 });
      return;
    }
    try {
      const key = `curriculum_plans_${userId}`;
      const plans = engine.get(key, null);
      if (!Array.isArray(plans) || plans.length === 0) {
        setTodayPlan({ hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0 });
        return;
      }
      const active = plans.find((p) => p.pinned) || plans[0];
      const age = Date.now() - new Date(active.createdAt || Date.now()).getTime();
      if (age > 14 * 24 * 60 * 60 * 1000) {
        setTodayPlan({ hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0 });
        return;
      }
      const todayIdx = getTodayPersianDayIndex();
      const today = active.days?.[todayIdx];
      const sessions = Array.isArray(today?.sessions) ? today.sessions : [];
      if (sessions.length === 0) {
        setTodayPlan({ hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0 });
        return;
      }
      const doneCount = sessions.filter((s) => s.done).length;
      const totalCount = sessions.length;
      setTodayPlan({
        hasPlan: true,
        planId: active.id,
        planTitle: active.title,
        dayIndex: todayIdx,
        dayName: today?.day || '',
        sessions,
        doneCount,
        totalCount,
        allDone: doneCount === totalCount,
      });
    } catch (e) {
      console.warn('Failed to read today plan:', e);
      setTodayPlan({ hasPlan: false, sessions: [], allDone: false, doneCount: 0, totalCount: 0 });
    }
  }, [userId]);

  useEffect(() => { readPlan(); }, [readPlan, tick]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key && e.key.startsWith('curriculum_plans_')) readPlan();
    };
    const onFocus = () => readPlan();
    const onVisibility = () => { if (!document.hidden) readPlan(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [readPlan]);

  const markSessionDone = useCallback((sessionId) => {
    if (!userId) return;
    try {
      const key = `curriculum_plans_${userId}`;
      const plans = engine.get(key, null);
      if (!Array.isArray(plans)) return;
      const active = plans.find((p) => p.pinned) || plans[0];
      if (!active) return;
      const todayIdx = getTodayPersianDayIndex();
      const sessions = active.days?.[todayIdx]?.sessions || [];
      const target = sessions.find((s) => s.id === sessionId);
      if (!target || target.done) return;
      target.done = true;
      engine.set(key, plans);
      setTick((k) => k + 1);
    } catch (e) {
      console.warn('Failed to mark session done:', e);
    }
  }, [userId]);

  return { todayPlan, markSessionDone };
};

// ---- useContinueLearning ----
const useContinueLearning = (userId) => {
  const [state, setState] = useState({
    mode: 'loading', lesson: null, book: null, othersCount: 0,
  });

  useEffect(() => {
    if (!userId || userId === 'unknown') {
      setState({ mode: 'empty', lesson: null, book: null, othersCount: 0 });
      return;
    }
    const load = () => {
      try {
        const records = engine.get(`lesson-records-${userId}`, null);
        if (!records || typeof records !== 'object') {
          setState({ mode: 'empty', lesson: null, book: null, othersCount: 0 });
          return;
        }

        const prefix = `lesson-progress-${userId}-`;
        const progressIndex = {};
        const keys = engine.keys(prefix);
        for (const k of keys) {
          try {
            const p = engine.get(k, {});
            progressIndex[k] = Array.isArray(p.completedSteps) ? p.completedSteps.length : 0;
          } catch { /* ignore */ }
        }

        const incomplete = [];
        const booksMap = {};

        for (const rec of Object.values(records)) {
          if (!rec) continue;
          const { grade, subject, folderName, lessonId, lessonTitle, image, teacherName } = rec;
          if (!grade || !subject || !folderName || !lessonId) continue;
          const total = Number(rec.totalSteps) || 0;
          if (total <= 0) continue;

          const pKey = `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${lessonId}`;
          const done = progressIndex[pKey] || 0;
          const lv = Number(rec.lastVisited) || 0;
          const percent = Math.min(100, Math.round((done / total) * 100));

          if (done > 0 && done < total) {
            incomplete.push({
              grade: String(grade),
              subject: String(subject),
              folderName: String(folderName),
              lessonId: String(lessonId),
              lessonTitle: lessonTitle || lessonId,
              image: image || '',
              teacherName: teacherName || '',
              totalSteps: total,
              completedCount: done,
              percent,
              lastVisited: lv,
            });
          }

          if (done >= total) {
            const bookKey = `${grade}|${subject}|${folderName}`;
            if (!booksMap[bookKey] || booksMap[bookKey].lastVisited < lv) {
              booksMap[bookKey] = {
                grade: String(grade),
                subject: String(subject),
                folderName: String(folderName),
                lessonId: String(lessonId),
                lessonTitle: lessonTitle || lessonId,
                teacherName: teacherName || '',
                lastVisited: lv,
                percent,
                isComplete: true,
              };
            }
          }
        }

        if (incomplete.length > 0) {
          const bestByLesson = {};
          for (const c of incomplete) {
            const key = `${c.grade}|${c.subject}|${c.folderName}|${c.lessonId}`;
            if (!bestByLesson[key] || bestByLesson[key].lastVisited < c.lastVisited) {
              bestByLesson[key] = c;
            }
          }
          const sorted = Object.values(bestByLesson).sort((a, b) => b.lastVisited - a.lastVisited);
          setState({ mode: 'continue', lesson: sorted[0], book: null, othersCount: Math.max(0, sorted.length - 1) });
          return;
        }

        const sortedBooks = Object.values(booksMap).sort((a, b) => b.lastVisited - a.lastVisited);
        if (sortedBooks.length > 0) {
          setState({ mode: 'next-lesson', lesson: null, book: sortedBooks[0], othersCount: Math.max(0, sortedBooks.length - 1) });
          return;
        }

        setState({ mode: 'empty', lesson: null, book: null, othersCount: 0 });
      } catch (e) {
        console.warn('useContinueLearning error:', e);
        setState({ mode: 'empty', lesson: null, book: null, othersCount: 0 });
      }
    };

    load();
    const onStorage = (e) => {
      if (!e.key) return load();
      if (e.key === `lesson-records-${userId}`) return load();
      if (e.key.startsWith(`lesson-progress-${userId}-`)) return load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId]);

  return state;
};

// ---- useMyCourses ----
const useMyCourses = (userId, userGrade) => {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const gradeNum = normalizeGrade(userGrade);
    if (!userId || userId === 'unknown' || !gradeNum) {
      setCourses([]);
      return;
    }
    const load = () => {
      try {
        const books = Object.entries(COURSE_DETAILS)
          .filter(([key]) => key.startsWith(`${gradeNum}-`))
          .map(([key, data]) => {
            const subject = key.split('-')[1];
            return {
              key,
              grade: gradeNum,
              subject,
              title: data.title,
              totalLessons: Number(data.lessons) || 0,
              color: SUBJECT_COLORS[subject] || '#8b5cf6',
              emoji: SUBJECT_EMOJI[subject] || '📚',
            };
          });

        const prefix = `lesson-progress-${userId}-`;
        const progressIndex = {};
        const keys = engine.keys(prefix);
        for (const k of keys) {
          try {
            const p = engine.get(k, {});
            progressIndex[k] = (p.completedSteps || []).length;
          } catch { /* ignore */ }
        }

        const records = engine.get(`lesson-records-${userId}`, {});

        const result = books.map((book) => {
          const matching = Object.values(records).filter(
            (rec) => normalizeGrade(rec.grade) === gradeNum && rec.subject === book.subject
          );
          const lessonBest = {};
          for (const rec of matching) {
            const { folderName, lessonId, totalSteps } = rec;
            if (!lessonId || !totalSteps) continue;
            const pKey = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${folderName}-${lessonId}`;
            const completed = progressIndex[pKey] || 0;
            const ratio = Math.min(1, completed / totalSteps);
            if (!lessonBest[lessonId] || lessonBest[lessonId] < ratio) {
              lessonBest[lessonId] = ratio;
            }
          }
          const sumRatios = Object.values(lessonBest).reduce((s, r) => s + r, 0);
          const percent = book.totalLessons > 0
            ? Math.min(100, Math.round((sumRatios / book.totalLessons) * 100))
            : 0;
          return { ...book, percent };
        });

        result.sort((a, b) => {
          if (b.percent !== a.percent) return b.percent - a.percent;
          return a.title.localeCompare(b.title, 'fa');
        });
        setCourses(result);
      } catch (e) {
        console.warn('useMyCourses error:', e);
        setCourses([]);
      }
    };
    load();
    const onStorage = (e) => {
      if (e.key && (e.key.startsWith('lesson-records-') || e.key.startsWith('lesson-progress-'))) load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, userGrade]);

  return courses;
};

// ---- useStudyStats ----
const useStudyStats = (userId) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    streak: 0, todayMinutes: 0, weekMinutes: [0, 0, 0, 0, 0, 0, 0], booksCount: 0,
  });

  useEffect(() => {
    if (!userId || userId === 'unknown') return;
    const load = () => {
      let streakVal = 0;
      try {
        const d = engine.get(`streak-${userId}`, null);
        if (d) {
          const today = new Date().toISOString().split('T')[0];
          const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          const last = d.lastDate || d.lastActivityDate;
          if (last === today || last === yesterday) {
            streakVal = Number(d.streak) || 0;
          }
        }
      } catch { /* ignore */ }

      let todayMinutes = 0;
      try {
        const daily = user?.dailyProgress;
        if (daily && typeof daily === 'object') {
          const today = new Date().toISOString().split('T')[0];
          todayMinutes = Number(daily[today]) || 0;
        }
      } catch { /* ignore */ }

      let weekMinutes = [0, 0, 0, 0, 0, 0, 0];
      try {
        const arr = user?.weeklyProgress;
        if (Array.isArray(arr) && arr.length === 7) {
          weekMinutes = arr.map((n) => Number(n) || 0);
        }
      } catch { /* ignore */ }

      let booksCount = 0;
      try {
        const books = user?.bookStats;
        if (books && typeof books === 'object') {
          booksCount = Object.values(books).filter((m) => Number(m) > 0).length;
        }
      } catch { /* ignore */ }

      setStats({ streak: streakVal, todayMinutes, weekMinutes, booksCount });
    };
    load();
    const onStorage = (e) => {
      if (e.key && (
        e.key.startsWith('dailyProgress_') ||
        e.key.startsWith('weeklyProgress_') ||
        e.key.startsWith('monthlyProgress_') ||
        e.key.startsWith('bookStats_') ||
        e.key.startsWith('streak_')
      )) load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, user]);

  return stats;
};

// ---- useChallengeStats ----
const useChallengeStats = (userId, userGrade) => {
  const { coins, xp } = useCoins();
  const { exams } = useExams();

const [stats, setStats] = useState({
  streak: 0,
  completedChallenges: 0,
  activeChallenges: 0,
  dailyChallenge: null,
  dailyProgress: 0,
  dragon: DRAGONS[0],
});
  useEffect(() => {
    if (!userId || userId === 'unknown') return;
    const load = () => {
      let streakVal = 0;
      try {
        const data = engine.get(`streak-${userId}`, null);
        if (data) {
          const today = new Date().toISOString().split('T')[0];
          const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          if (data.lastActivityDate === today || data.lastActivityDate === yesterday) {
            streakVal = Number(data.streak) || 0;
          }
        }
      } catch { /* ignore */ }

      let completedVal = 0;
      try {
        const raw = engine.get(`completedChallenges_${userId}`, null);
        if (raw != null) completedVal = Number(raw) || 0;
      } catch { /* ignore */ }

      let daily = null;
      let dailyProgress = 0;
      try {
        const data = engine.get(`dailyChallenge_${userId}`, null);
        if (data) {
          const today = new Date().toISOString().split('T')[0];
          if (data.date === today) {
            daily = data;
            dailyProgress = Number(data.progress) || 0;
          }
        }
      } catch { /* ignore */ }

      const gradeNum = normalizeGrade(userGrade);
      const active = (exams || []).filter((e) => {
        if (!e.isChallenge) return false;
        if (e.statusType === 'archived' || e.statusType === 'pending') return false;
        if (gradeNum) {
          const eg = normalizeGrade(e.grade);
          if (eg && eg !== gradeNum) return false;
        }
        const result = e.results?.find((r) => r.studentId === userId);
        if (result?.isFinalSubmit === true) return false;
        return true;
      });

      const dragon = getDragonForXp(xp || 0);

      setStats({
        streak: streakVal,
        completedChallenges: completedVal,
        activeChallenges: active.length,
        dailyChallenge: daily,
        dailyProgress,
        dragon,
      });
    };
    load();
    const onStorage = (e) => {
      if (e.key && (
        e.key.startsWith('streak_') ||
        e.key.startsWith('completedChallenges_') ||
        e.key.startsWith('dailyChallenge_') ||
        e.key === 'exams'
      )) load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, userGrade, exams, xp]);

  return { ...stats, coins, xp };
};

// ---- useParentStats ----
const computeRealPerformance = (userId, userGrade) => {
  const gradeNum = normalizeGrade(userGrade);
  if (!userId || !gradeNum) return { percent: 0, coverage: 0, depth: 0 };

  const books = Object.entries(COURSE_DETAILS).filter(([key]) => key.startsWith(`${gradeNum}-`));
  if (books.length === 0) return { percent: 0, coverage: 0, depth: 0 };

  const prefix = `lesson-progress-${userId}-`;
  const progressIndex = {};
  const keys = engine.keys(prefix);
  for (const k of keys) {
    try {
      const p = engine.get(k, {});
      progressIndex[k] = Array.isArray(p.completedSteps) ? p.completedSteps.length : 0;
    } catch { /* ignore */ }
  }
  const records = engine.get(`lesson-records-${userId}`, {});

  const bookStats = {};
  for (const [key, data] of books) {
    const subject = key.split('-')[1];
    bookStats[subject] = {
      attemptedLessons: new Set(),
      masteryByLesson: {},
      totalLessons: Number(data.lessons) || 0,
    };
  }

  for (const rec of Object.values(records)) {
    if (!rec) continue;
    const recGrade = normalizeGrade(rec.grade);
    if (recGrade !== gradeNum) continue;
    const { subject, folderName, lessonId, totalSteps } = rec;
    if (!bookStats[subject] || !lessonId || !totalSteps) continue;
    const pKey = `lesson-progress-${userId}-${rec.grade}-${subject}-${folderName}-${lessonId}`;
    const done = progressIndex[pKey] || 0;
    if (done <= 0) continue;
    const ratio = Math.min(1, done / totalSteps);
    const stats = bookStats[subject];
    stats.attemptedLessons.add(lessonId);
    if (!stats.masteryByLesson[lessonId] || stats.masteryByLesson[lessonId] < ratio) {
      stats.masteryByLesson[lessonId] = ratio;
    }
  }

  let totalCoverage = 0, totalDepth = 0, validBooks = 0;
  for (const subject in bookStats) {
    const stats = bookStats[subject];
    if (stats.totalLessons <= 0) continue;
    const coverage = stats.attemptedLessons.size / stats.totalLessons;
    const masteries = Object.values(stats.masteryByLesson);
    const depth = masteries.length > 0 ? masteries.reduce((s, m) => s + m, 0) / masteries.length : 0;
    totalCoverage += coverage;
    totalDepth += depth;
    validBooks++;
  }
  if (validBooks === 0) return { percent: 0, coverage: 0, depth: 0 };
  const avgCoverage = totalCoverage / validBooks;
  const avgDepth = totalDepth / validBooks;
  return {
    percent: Math.min(100, Math.round((avgCoverage * 0.5 + avgDepth * 0.5) * 100)),
    coverage: Math.round(avgCoverage * 100),
    depth: Math.round(avgDepth * 100),
  };
};

const computeRealExamAvg = (userId, allExams) => {
  if (!userId || !Array.isArray(allExams)) return { percent: 0, count: 0 };
  const now = Date.now();
  const THIRTY = 30 * 24 * 60 * 60 * 1000;
  const SIXTY = 60 * 24 * 60 * 60 * 1000;
  const weighted = [];

  for (const exam of allExams) {
    const result = exam.results?.find(
      (r) => r.studentId === userId && r.isFinalSubmit === true
    );
    if (!result || result.isAbsent === true) continue;
    const maxScore = exam.questions?.reduce((s, q) => s + (Number(q.score) || 1), 0) || 0;
    if (maxScore <= 0) continue;
    const percent = Math.min(100, ((Number(result.score) || 0) / maxScore) * 100);
    const submittedAt = result.submittedAt ? new Date(result.submittedAt).getTime() : 0;
    const age = now - submittedAt;
    let recencyWeight = 0.3;
    if (age <= THIRTY) recencyWeight = 1.0;
    else if (age <= SIXTY) recencyWeight = 0.6;
    const questionCount = exam.questions?.length || 0;
    const sizeWeight = Math.min(2.0, Math.max(0.5, questionCount / 15));
    const typeWeight = exam.isGlobal ? 0.8 : 1.2;
    weighted.push({ percent, weight: recencyWeight * sizeWeight * typeWeight });
  }
  if (weighted.length === 0) return { percent: 0, count: 0 };
  const totalWeight = weighted.reduce((s, w) => s + w.weight, 0);
  const weightedSum = weighted.reduce((s, w) => s + w.percent * w.weight, 0);
  return { percent: Math.round(weightedSum / totalWeight), count: weighted.length };
};

const computeRealStudy = (userId, userGrade, user) => {
  if (!userId) return { percent: 0, minutes: 0, goal: 300, breakdown: {} };
  const now = new Date();
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    last7Days.push(d.toISOString().split('T')[0]);
  }
  let studyTimerMinutes = 0;
  try {
    const daily = user?.dailyProgress;
    if (daily && typeof daily === 'object') {
      last7Days.forEach((date) => {
        studyTimerMinutes += Number(daily[date]) || 0;
      });
    }
  } catch { /* ignore */ }

  let planMinutes = 0;
  try {
    const allPlans = engine.get(`curriculum_plans_${userId}`, null);
    if (Array.isArray(allPlans)) {
      const active = allPlans.find((p) => p.pinned) || allPlans[0];
      if (active?.days) {
        active.days.forEach((day) => {
          (day.sessions || []).forEach((s) => {
            if (s.done) planMinutes += Number(s.duration) || 45;
          });
        });
      }
    }
  } catch { /* ignore */ }

  const totalMinutes = studyTimerMinutes + planMinutes;
  const gradeNum = normalizeGrade(userGrade);
  let weeklyGoal = 300;
  if (['1', '2'].includes(gradeNum)) weeklyGoal = 120;
  else if (['3', '4'].includes(gradeNum)) weeklyGoal = 180;
  else if (['5', '6'].includes(gradeNum)) weeklyGoal = 300;
  else if (['7', '8', '9'].includes(gradeNum)) weeklyGoal = 420;
  else if (gradeNum) weeklyGoal = 600;

  return {
    percent: Math.min(100, Math.round((totalMinutes / weeklyGoal) * 100)),
    minutes: totalMinutes,
    goal: weeklyGoal,
    breakdown: { studyTimer: studyTimerMinutes, lessonPlanning: planMinutes },
  };
};

const useParentStats = (userId, userGrade) => {
  const { user } = useAuth();
  const { exams } = useExams();
  const [stats, setStats] = useState({
    performance: 0, exams: 0, study: 0, total: 0,
    details: {
      performance: { coverage: 0, depth: 0 },
      exams: { count: 0 },
      study: { minutes: 0, goal: 300, breakdown: {} },
      streak: 0,
    },
  });

  useEffect(() => {
    if (!userId || userId === 'unknown') return;
    const load = () => {
      const perf = computeRealPerformance(userId, userGrade);
      const allExams = exams || engine.get('exams', []) || [];
      const examData = computeRealExamAvg(userId, allExams);
      const studyData = computeRealStudy(userId, userGrade, user);

      let streakVal = 0;
      try {
        const d = engine.get(`streak-${userId}`, null);
        if (d) {
          const today = new Date().toISOString().split('T')[0];
          const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          const last = d.lastDate || d.lastActivityDate;
          if (last === today || last === yesterday) {
            streakVal = Number(d.streak) || 0;
          }
        }
      } catch { /* ignore */ }

      const base = perf.percent * 0.40 + examData.percent * 0.35 + studyData.percent * 0.25;
      const streakBonus = Math.min(10, streakVal * 1);
      const total = Math.min(100, Math.round(base + streakBonus));

      setStats({
        performance: perf.percent,
        exams: examData.percent,
        study: studyData.percent,
        total,
        details: {
          performance: { coverage: perf.coverage, depth: perf.depth },
          exams: { count: examData.count },
          study: { minutes: studyData.minutes, goal: studyData.goal, breakdown: studyData.breakdown },
          streak: streakVal,
        },
      });
    };
    load();
    const onStorage = (e) => {
      if (!e.key) return load();
      const watched = [
        'lesson-records-', 'lesson-progress-',
        'dailyProgress_', 'weeklyProgress_',
        'curriculum_plans_', 'streak_', 'exams',
      ];
      if (watched.some((w) => e.key === w || e.key.startsWith(w))) load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, userGrade, exams, user]);

  return stats;
};








// ================================================================
// Book progress — with quiz-passed union (دقیقاً مثل ParentDashboard)
// ================================================================
const computeBookProgressWithQuiz = (userId, userGrade) => {
  const gradeNum = normalizeGrade(userGrade);
  if (!userId || !gradeNum) return 0;

  const records = engine.get(`lesson-records-${userId}`, {}) || {};
  const progressIndex = {};
  try {
    engine.keys(`lesson-progress-${userId}-`).forEach((k) => {
      const p = engine.get(k, {}) || {};
      progressIndex[k] = new Set(p.completedStepIds || []);
    });
  } catch { /* ignore */ }

  const lessonRatios = [];
  for (const rec of Object.values(records)) {
    if (!rec || !rec.grade || !rec.subject || !rec.folderName || !rec.lessonId) continue;
    if (normalizeGrade(rec.grade) !== gradeNum) continue;
    const total = Number(rec.totalSteps) || 0;
    if (total <= 0) continue;

    const key = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
    const completedSet = progressIndex[key] || new Set();

    const passedQuizIds = new Set();
    if (rec.projectId && Array.isArray(rec.quizStepIds) && rec.quizStepIds.length > 0) {
      try {
        const qp = engine.get(`step-quiz-progress-${userId}-${rec.projectId}`, {}) || {};
        rec.quizStepIds.forEach((id) => {
          if (qp[id]?.passed === true) passedQuizIds.add(id);
        });
      } catch { /* ignore */ }
    }

    const allDone = new Set([...completedSet, ...passedQuizIds]);
    const done = Math.min(total, allDone.size);
    lessonRatios.push(done / total);
  }

  if (lessonRatios.length === 0) return 0;
  return (lessonRatios.reduce((a, b) => a + b, 0) / lessonRatios.length) * 100;
};

// ================================================================
// Exam average — simple (دقیقاً مثل ParentDashboard)
// ================================================================
const computeExamSimpleAvg = (userId, allExams) => {
  if (!userId || !Array.isArray(allExams)) return 0;
  const percents = [];
  for (const exam of allExams) {
    if (!exam || exam.isChallenge || exam.isStepQuiz) continue;
    if (exam.statusType === 'archived') continue;
    const r = exam.results?.find((x) => String(x.studentId) === String(userId));
    if (!r || r.isFinalSubmit !== true || r.isAbsent === true) continue;
    const max = (exam.questions || []).reduce((s, q) => s + (Number(q.score) || 1), 0);
    if (max <= 0) continue;
    percents.push(Math.min(100, (Number(r.score) / max) * 100));
  }
  if (percents.length === 0) return 0;
  return percents.reduce((a, b) => a + b, 0) / percents.length;
};





// ================================================================
// Weekly Progress — weighted score (40/40/20)
// ================================================================
const computeWeeklyOverallProgress = (userId, userGrade, exams, weeklyStudyMinutes) => {
  const gradeNum = normalizeGrade(userGrade);
  if (!userId || !gradeNum) return 0;

  // دقیقاً مثل ParentDashboard:
  const bookProgressPct = computeBookProgressWithQuiz(userId, userGrade);
  const examAvgPct = computeExamSimpleAvg(userId, exams || []);

  const weeklyGoal = 300; // هاردکد مثل ParentDashboard
  const studyScorePct = weeklyGoal > 0
    ? Math.min(100, (weeklyStudyMinutes / weeklyGoal) * 100)
    : 0;

  const total =
    bookProgressPct * 0.40 +
    examAvgPct * 0.40 +
    studyScorePct * 0.20;

  return Math.min(100, Math.max(0, Math.round(total)));
};

// ================================================================
// Weekly Progress — hook (همیشه هفتگی)
// ================================================================
const useWeeklyProgress = (userId, userGrade) => {
  const { user } = useAuth();
  const { exams } = useExams();

  const [data, setData] = useState({
    overallProgress: 0,
    overallSeries: [],
    totalStudyMinutes: 0,
  });

  useEffect(() => {
    if (!userId || userId === 'unknown') return;

    const load = () => {
      try {
        // ── ۱. ساخت ۷ باکت روزانه (از ۶ روز پیش تا امروز)
        const now = new Date();
        const todayStart = startOfDayLocal(now);
        const todayKey = ymdLocal(now);
        const buckets = [];
        for (let i = 6; i >= 0; i--) {
          const s = addDaysLocal(todayStart, -i);
          const e = addDaysLocal(s, 1);
          const isToday = ymdLocal(s) === todayKey;
          let dayLabel;
          try {
            dayLabel = isToday
              ? 'امروز'
              : new Intl.DateTimeFormat('fa-IR', { weekday: 'short' }).format(s);
          } catch {
            dayLabel = isToday ? 'امروز' : String(i);
          }
          buckets.push({ start: s, end: e, label: dayLabel });
        }

        // ── ۲. سری روزانه‌ی زمان مطالعه
        const daily = user?.dailyProgress || {};
        const studySeries = buckets.map((b) => {
          let sum = 0;
          const cur = new Date(b.start);
          while (cur < b.end) {
            sum += Number(daily[ymdLocal(cur)]) || 0;
            cur.setDate(cur.getDate() + 1);
          }
          return { label: b.label, value: sum };
        });

        // ── ۳. سری روزانه (هر نقطه = فقط همان روز، بدون تجمع)
        const overallSeries = studySeries.map((d) => ({
          label: d.label,
          value: Number(d.value) || 0,
        }));

const totalStudyMinutes = studySeries.reduce(
  (s, d) => s + (Number(d.value) || 0),
  0
);

// ── ۴. امتیاز وزنی (۴۰/۴۰/۲۰) — از همان دقیقه‌های نمودار
const overallProgress = computeWeeklyOverallProgress(
  userId,
  userGrade,
  exams,
  totalStudyMinutes   // ← دقیقه‌های همین نمودار
);

setData({ overallProgress, overallSeries, totalStudyMinutes });
      } catch (e) {
        console.warn('useWeeklyProgress error:', e);
      }
    };

    load();

    const onStorage = (e) => {
      if (!e.key) return load();
      const watched = [
        'dailyProgress_',
        'lesson-records-',
        'lesson-progress-',
        'step-quiz-progress-',
        'exams',
      ];
      if (watched.some((w) => e.key === w || e.key.startsWith(w))) load();
    };
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, userGrade, user, exams]);

  return data;
};

// ================================================================
// Weekly Progress — Y-Axis
// ================================================================
const WpYAxis = ({ values, padTop = 12, padBottom = 8, H = 180 }) => {
  const chartH = H - padTop - padBottom;
  return (
    <div className="wp-y-axis">
      {values.map((v, i) => {
        const f = values.length > 1 ? i / (values.length - 1) : 0;
        const topPct = ((padTop + f * chartH) / H) * 100;
        return (
          <span key={i} className="wp-y-tick" style={{ top: `${topPct}%` }}>
            {toPersianDigits(Math.round(v))}
          </span>
        );
      })}
    </div>
  );
};

// ================================================================
// Weekly Progress — Line Chart
// ================================================================
const WpLineChart = ({ series, color = '#6366F1' }) => {
  const plotRef = useRef(null);
  const [tip, setTip] = useState(null);
  const gradId = useMemo(
    () => 'wpg-' + Math.random().toString(36).slice(2, 9),
    []
  );

  const W = 500;
  const H = 180;
  const padTop = 12;
  const padBottom = 8;
  const chartH = H - padTop - padBottom;

  if (!series || series.length === 0) {
    return <div className="wp-chart-empty">هنوز زمانی برای مطالعه ثبت نشده</div>;
  }

  const values = series.map((s) => Number(s.value) || 0);
  const yTop = niceMax(Math.max(...values, 1));
  const yBot = 0;
  const n = series.length;
  const stepX = n > 1 ? (W - 12) / (n - 1) : 0;

  const pts = series.map((s, i) => {
    const v = Number(s.value) || 0;
    const x = W - 6 - i * stepX;
    const ratio = yTop === yBot ? 0 : (v - yBot) / (yTop - yBot);
    const y = padTop + chartH - Math.max(0, Math.min(1, ratio)) * chartH;
    return { x, y, v, label: s.label };
  });

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const t = 0.2;
    d +=
      ` C ${p1.x + (p2.x - p0.x) * t} ${p1.y + (p2.y - p0.y) * t}, ` +
      `${p2.x - (p3.x - p1.x) * t} ${p2.y - (p3.y - p1.y) * t}, ` +
      `${p2.x} ${p2.y}`;
  }
  const area = `${d} L ${pts[n - 1].x} ${padTop + chartH} L ${pts[0].x} ${padTop + chartH} Z`;

  const handleMove = (e) => {
    if (!plotRef.current) return;
    const rect = plotRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0;
    let dist = Infinity;
    pts.forEach((p, i) => {
      const dd = Math.abs(p.x - svgX);
      if (dd < dist) { dist = dd; nearest = i; }
    });
    const p = pts[nearest];
    setTip({
      x: (p.x / W) * rect.width,
      y: (p.y / H) * rect.height,
      label: p.label,
      value: `${toPersianDigits(Math.round(p.v))} دقیقه`,
    });
  };

  const yTicks = [yTop, yTop * 0.75, yTop * 0.5, yTop * 0.25, yBot];

  return (
    <>
      <div className="wp-chart-body-inner">
        <WpYAxis values={yTicks} padTop={padTop} padBottom={padBottom} H={H} />
        <div
          className="wp-chart-plot"
          ref={plotRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setTip(null)}
          onTouchStart={(e) => { if (e.touches[0]) handleMove(e.touches[0]); }}
          onTouchMove={(e) => { if (e.touches[0]) handleMove(e.touches[0]); }}
          onTouchEnd={() => setTip(null)}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="wp-chart-svg"
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <g>
              {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
                <line
                  key={i}
                  x1="0"
                  y1={padTop + f * chartH}
                  x2={W}
                  y2={padTop + f * chartH}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  strokeDasharray="3 5"
                />
              ))}
            </g>
            <path d={area} fill={`url(#${gradId})`} />
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {pts.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={tip && tip.label === p.label ? 5 : 3.5}
                fill="var(--card)"
                stroke={color}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          {tip && (
            <div className="wp-chart-tooltip" style={{ left: tip.x, top: tip.y }}>
              <div className="wp-tt-label">{tip.label}</div>
              <div className="wp-tt-value">{tip.value}</div>
            </div>
          )}
        </div>
      </div>
      <div className="wp-chart-xaxis">
        {pts.map((p, i) => (
          <span
            key={i}
            className="wp-xaxis-label"
            style={{ left: `${(p.x / W) * 100}%` }}
          >
            {series[i]?.label}
          </span>
        ))}
      </div>
    </>
  );
};

// ================================================================
// Weekly Progress Card — جایگزین ParentCard
// ================================================================
const WeeklyProgressCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { overallProgress, overallSeries } = useWeeklyProgress(
    user?.id,
    user?.grade
  );

return (
  <div
    className="weekly-progress-card"
    onClick={() => navigate('/parent-dashboard')}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        navigate('/parent-dashboard');
      }
    }}
  >
      <div className="wp-header">
        <div className="wp-header-left">
          <div className="wp-header-icon">
            <i className="fas fa-chart-line" />
          </div>
          <div className="wp-header-titles">
            <div className="wp-title">پیشرفت کلی</div>
            <div className="wp-sub">زمان مطالعه روزانه · هفت روز اخیر</div>
          </div>
        </div>
        <div
          className="wp-header-right"
          title="۴۰٪ پیشرفت درس‌ها + ۴۰٪ میانگین آزمون‌ها + ۲۰٪ مطالعه‌ی ۷ روز اخیر"
        >
          <div className="wp-value">
            {toPersianDigits(overallProgress)}
            <span className="wp-value-suffix">٪</span>
          </div>
          <div className="wp-delta">پیشرفت تحصیلی کلی</div>
        </div>
      </div>

      <div className="wp-chart-body">
        <WpLineChart series={overallSeries} color="#6366F1" />
      </div>
    </div>
  );
};









// ================================================================
// Session Timer Overlay
// ================================================================
const SessionTimerOverlay = ({
  session, seconds, totalSeconds, running, paused, finished,
  onStart, onPause, onResume, onReset, onClose,
}) => {
  const percent = totalSeconds > 0 ? ((totalSeconds - seconds) / totalSeconds) * 100 : 0;
  const R = 110;
  const CIRC = 2 * Math.PI * R;
  const dashOffset = CIRC * (1 - percent / 100);
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  const timeStr = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  return (
    <div className="session-timer-overlay">
      <div className="session-timer-bg">
        <span className="session-timer-orb o1" />
        <span className="session-timer-orb o2" />
        <span className="session-timer-grid" />
      </div>

      <button className="session-timer-close" onClick={onClose} aria-label="بستن" type="button">
        <i className="fas fa-xmark" />
      </button>

      <div className="session-timer-content">
        <div className="session-timer-head">
          <span className="session-timer-tag">
            <i className="fas fa-book-open" /> در حال مطالعه
          </span>
          <h2 className="session-timer-book">{session?.book || 'مطالعه'}</h2>
          {session?.topic && <p className="session-timer-topic">{session.topic}</p>}
        </div>

        <div className="session-timer-ring-wrap">
          <svg className="session-timer-ring" viewBox="0 0 240 240">
            <defs>
              <linearGradient id="sessionTimerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="50%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
            <circle className="session-timer-track" cx="120" cy="120" r={R} />
            <circle
              className="session-timer-progress"
              cx="120" cy="120" r={R}
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <div className="session-timer-inner">
            {!finished ? (
              <>
                <div className="session-timer-time">{timeStr}</div>
                <div className="session-timer-status">
                  {running ? '⏳ در حال تمرکز' : paused ? '⏸ متوقف شده' : '▶ آماده شروع'}
                </div>
              </>
            ) : (
              <>
                <div className="session-timer-done">🎉</div>
                <div className="session-timer-done-text">عالی بود!</div>
                <div className="session-timer-done-sub">جلسه با موفقیت ثبت شد</div>
              </>
            )}
          </div>
        </div>

        <div className="session-timer-controls">
          {!finished && (
            <>
              {!running && !paused && (
                <button className="session-timer-btn primary" onClick={onStart} type="button">
                  <i className="fas fa-play" /> شروع
                </button>
              )}
              {running && !paused && (
                <button className="session-timer-btn warn" onClick={onPause} type="button">
                  <i className="fas fa-pause" /> توقف
                </button>
              )}
              {paused && (
                <button className="session-timer-btn success" onClick={onResume} type="button">
                  <i className="fas fa-play" /> ادامه
                </button>
              )}
              <button className="session-timer-btn ghost" onClick={onReset} aria-label="ریست" type="button">
                <i className="fas fa-rotate-right" />
              </button>
            </>
          )}
        </div>

        <div className="session-timer-meta">
          <span><i className="fas fa-clock" /> {session?.time}</span>
          <span><i className="fas fa-stopwatch" /> {Math.round(totalSeconds / 60)} دقیقه</span>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// HUD
// ================================================================
const HUD = ({ user, streak, level }) => {
  const navigate = useNavigate();
  const avatar = user?.avatar && String(user.avatar).trim();
  const isImage = avatar && (avatar.startsWith('/') || avatar.startsWith('http'));
  const fullName = user?.firstName || user?.name || user?.username || 'دوست من';

  return (
    <div className="hud">
      <div className="hud-user">
        <div
          className="hud-avatar"
          onClick={() => navigate('/avatars')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/avatar');
            }
          }}
          title="پروفایل و آواتار"
        >
          {isImage ? (
            <img src={avatar} alt={fullName} />
          ) : (
            <span>{avatar || '🧑'}</span>
          )}
        </div>
        <div className="hud-text">
          <div className="hud-hello">
            سلام <span className="name">{fullName}</span>! 👋
          </div>
          <div className="hud-sub">آماده‌ی یه روز عالی هستی؟</div>
        </div>
      </div>
      
    </div>
  );
};

// ================================================================
// HERO — merged states from StreakHero + ContinueBanner
// ================================================================
const Hero = ({ navigate, user }) => {
  const { todayPlan, markSessionDone } = useTodayPlan(user?.id);
  const continueState = useContinueLearning(user?.id);

  // Celebration
  const seenKey = user?.id
    ? `celebration_seen_${user.id}_${new Date().toDateString()}`
    : null;
  const [celebrationDismissed, setCelebrationDismissed] = useState(() => {
    if (!seenKey) return false;
    try { return engine.get(seenKey, null) === '1'; } catch { return false; }
  });
  const [showCelebration, setShowCelebration] = useState(false);
  const prevAllDone = useRef(false);

  // Timer
  const [timerOpen, setTimerOpen] = useState(false);
  const [timerSession, setTimerSession] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [timerFinished, setTimerFinished] = useState(false);
  const timerRef = useRef(null);
  const totalSecondsRef = useRef(0);

  const openTimer = useCallback((session) => {
    const duration = Number(session.duration) || 45;
    totalSecondsRef.current = duration * 60;
    setTimerSession(session);
    setTimerSeconds(duration * 60);
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerFinished(false);
    setTimerOpen(true);
  }, []);

  const closeTimer = useCallback(() => {
    setTimerOpen(false);
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerFinished(false);
    setTimerSession(null);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const startTimer = () => { setTimerRunning(true); setTimerPaused(false); };
  const pauseTimer = () => { setTimerRunning(false); setTimerPaused(true); };
  const resumeTimer = () => { setTimerRunning(true); setTimerPaused(false); };
  const resetTimer = () => {
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerFinished(false);
    setTimerSeconds(totalSecondsRef.current);
  };

  useEffect(() => {
    if (!timerOpen || !timerRunning || timerPaused) return;
    timerRef.current = setInterval(() => {
      setTimerSeconds((prev) => {
        if (prev <= 1) {
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          setTimerRunning(false);
          setTimerFinished(true);
          if (timerSession?.id) markSessionDone(timerSession.id);
          setTimeout(() => closeTimer(), 1800);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [timerOpen, timerRunning, timerPaused, timerSession, markSessionDone, closeTimer]);

  useEffect(() => {
    if (todayPlan.hasPlan && todayPlan.allDone && !prevAllDone.current && !celebrationDismissed) {
      const t = setTimeout(() => setShowCelebration(true), 350);
      prevAllDone.current = true;
      return () => clearTimeout(t);
    }
    if (!todayPlan.allDone) prevAllDone.current = false;
  }, [todayPlan.hasPlan, todayPlan.allDone, celebrationDismissed]);

  const dismissCelebration = () => {
    setShowCelebration(false);
    setCelebrationDismissed(true);
    if (seenKey) { try { engine.set(seenKey, '1'); } catch { /* ignore */ } }
  };

  // ---- Determine state ----
  const nextSession = useMemo(() => {
    if (!todayPlan.hasPlan) return null;
    return todayPlan.sessions.find((s) => !s.done) || null;
  }, [todayPlan]);

  let state;
  if (todayPlan.hasPlan && todayPlan.allDone && !celebrationDismissed && showCelebration) {
    state = 'celebration';
  } else if (todayPlan.hasPlan && !todayPlan.allDone && nextSession) {
    state = 'today';
  } else if (continueState.mode === 'continue' && continueState.lesson) {
    state = 'continue';
  } else if (continueState.mode === 'next-lesson' && continueState.book) {
    state = 'next';
  } else {
    state = 'start';
  }

  // ---- Per-state config ----
  let config;
  if (state === 'celebration') {
    config = {
      label: '🎉 امروز رو ترکوندی!',
      title: 'همه‌ی مأموریت‌های امروز انجام شد',
      sub: 'فردا هم منتظرتم!',
      mascot: '🏆',
      ctaText: 'دستاوردهام رو ببین',
      ctaIcon: 'fa-trophy',
      className: 'gold',
      showProgress: false,
      progress: 0,
      onCta: () => navigate('/medals'),
    };
  } else if (state === 'today') {
    config = {
      label: '⏰ جلسه‌ی بعدی امروز',
      title: nextSession.book || nextSession.topic || 'جلسه‌ی مطالعه',
      sub: `${nextSession.time || '—'} • ${nextSession.duration || 45} دقیقه`,
      mascot: '⏰',
      ctaText: 'بریم که شروع کنیم!',
      ctaIcon: 'fa-play',
      className: '',
      showProgress: false,
      progress: 0,
      onCta: () => openTimer(nextSession),
    };
  } else if (state === 'continue') {
    const lesson = continueState.lesson;
    config = {
      label: '📖 ادامه از جایی که رها کردی',
      title: lesson.lessonTitle,
      sub: `${lesson.teacherName ? lesson.teacherName + ' • ' : ''}فصل نیمه‌کاره`,
      mascot: '📖',
      ctaText: 'ادامه بده',
      ctaIcon: 'fa-arrow-left',
      className: '',
      showProgress: true,
      progress: lesson.percent,
      onCta: () => {
        const path = [
          '/Lessons-Gam',
          encodeURIComponent(lesson.grade),
          encodeURIComponent(lesson.subject),
          encodeURIComponent(lesson.folderName),
          encodeURIComponent(lesson.lessonId),
        ].join('/');
        navigate(path);
      },
    };
  } else if (state === 'next') {
    const book = continueState.book;
    config = {
      label: '🚀 آفرین! درس قبلی تموم شد',
      title: `درس بعدی: ${book.lessonTitle}`,
      sub: 'آماده‌ای برای شروع؟',
      mascot: '🚀',
      ctaText: 'شروع درس جدید',
      ctaIcon: 'fa-rocket',
      className: 'green',
      showProgress: false,
      progress: 100,
      onCta: () => {
        let teacherState;
        try {
          const tmap = engine.get(`teacher-map-${user?.id}`, {}) || {};
          const t = tmap[book.folderName];
          if (t) {
            teacherState = {
              teacher: {
                name: t.name || '',
                image: t.image || '',
                folderName: book.folderName,
              },
            };
          }
        } catch { /* ignore */ }
        navigate(`/book/${book.grade}/${book.subject}`, { state: teacherState });
      },
    };
  } else {
    config = {
      label: '🌟 اولین قدم',
      title: 'اولین درس رو شروع کن!',
      sub: 'هنوز هیچ درسی رو شروع نکردی',
      mascot: '🌟',
      ctaText: 'بریم که شروع کنیم',
      ctaIcon: 'fa-play',
      className: '',
      showProgress: false,
      progress: 0,
      onCta: () => navigate('/books'),
    };
  }

  const confettiPieces = useMemo(() => {
    if (state !== 'celebration') return [];
    const colors = ['#fbbf24', '#ec4899', '#8b5cf6', '#22c55e', '#3b82f6', '#f472b6'];
    return Array.from({ length: 24 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * -20,
      color: colors[i % colors.length],
      delay: Math.random() * 0.5,
      x: (Math.random() - 0.5) * 200,
      round: Math.random() > 0.5,
    }));
  }, [state]);

  return (
    <>
      <div className={`hero ${config.className} hero-margin`}>
        <div className="hero-label">{config.label}</div>
        <div className="hero-body">
          <div className="mascot">{config.mascot}</div>
          <div className="hero-text">
            <h2>{config.title}</h2>
            <p>{config.sub}</p>
          </div>
        </div>
        {config.showProgress && (
          <div className="hero-progress-wrap">
            <div className="hero-progress-labels">
              <span>پیشرفت درس</span>
              <span>{toPersianDigits(config.progress)}٪</span>
            </div>
            <div className="hero-progress">
              <div className="hero-progress-fill" style={{ width: `${config.progress}%` }} />
            </div>
          </div>
        )}
        <button className="hero-cta" onClick={config.onCta} type="button">
          <i className={`fas ${config.ctaIcon}`} />
          <span>{config.ctaText}</span>
        </button>
        {state === 'celebration' && (
          <div className="confetti" aria-hidden="true" onClick={dismissCelebration}>
            {confettiPieces.map((p) => (
              <div
                key={p.id}
                className="confetti-piece"
                style={{
                  left: `${p.left}%`,
                  top: `${p.top}px`,
                  background: p.color,
                  borderRadius: p.round ? '50%' : '2px',
                  '--delay': `${p.delay}s`,
                  '--x': `${p.x}px`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {timerOpen && createPortal(
        <div className="student-home">
          <SessionTimerOverlay
            session={timerSession}
            seconds={timerSeconds}
            totalSeconds={totalSecondsRef.current}
            running={timerRunning}
            paused={timerPaused}
            finished={timerFinished}
            onStart={startTimer}
            onPause={pauseTimer}
            onResume={resumeTimer}
            onReset={resetTimer}
            onClose={closeTimer}
          />
        </div>,
        document.body
      )}
    </>
  );
};

// ================================================================
// QUICK ACCESS
// ================================================================
const QUICK_ACCESS_ITEMS = [
  { id: 'books', label: 'درس‌ها', icon: 'fa-book', colorClass: 'qa-books', path: '/books' },
  { id: 'exams', label: 'آزمون‌ها', icon: 'fa-clipboard-list', colorClass: 'qa-exams', path: '/exams/student' },
  { id: 'plan', label: 'برنامه درسی', icon: 'fa-calendar-alt', colorClass: 'qa-plan', path: '/Lesson-planning' },
  { id: 'medals', label: 'افتخارات', icon: 'fa-medal', colorClass: 'qa-medals', path: '/medals' },
  { id: 'challenge', label: 'چالش', icon: 'fa-bolt', colorClass: 'qa-challenge', path: '/Challenge' },
  { id: 'timer', label: 'اتاق مطالعه', icon: 'fa-stopwatch', colorClass: 'qa-timer', path: '/StudyTimer' },
  
];

const QuickAccess = ({ navigate }) => (
  <div className="quick-access">
    <div className="qa-header">
      <div className="qa-header-icon"><i className="fas fa-bolt" /></div>
      <div className="qa-header-title">میانبرهای سریع</div>
      <span className="qa-header-badge">۶ میانبر</span>
    </div>
    <div className="qa-grid">
      {QUICK_ACCESS_ITEMS.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`qa-item ${item.colorClass}`}
          onClick={() => navigate(item.path)}
        >
          <div className="qa-orb"><i className={`fas ${item.icon}`} /></div>
          <div className="qa-label">{item.label}</div>
        </button>
      ))}
    </div>
  </div>
);









// ================================================================
// پیدا کردن آخرین درس نیمه‌کاره در یک دوره (برای دکمه «ادامه»)
// ================================================================
const getLastIncompleteLesson = (userId, grade, subject) => {
  if (!userId || userId === 'unknown') return null;
  const gradeNum = normalizeGrade(grade);
  if (!gradeNum) return null;

  try {
    const records = engine.get(`lesson-records-${userId}`, {});
    if (!records || typeof records !== 'object') return null;

    const progressIndex = {};
    try {
      const keys = engine.keys(`lesson-progress-${userId}-`);
      for (const k of keys) {
        const p = engine.get(k, {});
        progressIndex[k] = Array.isArray(p.completedSteps) ? p.completedSteps.length : 0;
      }
    } catch { /* ignore */ }

    const incomplete = [];
    for (const rec of Object.values(records)) {
      if (!rec) continue;
      if (normalizeGrade(rec.grade) !== gradeNum) continue;
      if (rec.subject !== subject) continue;
      if (!rec.folderName || !rec.lessonId) continue;
      const total = Number(rec.totalSteps) || 0;
      if (total <= 0) continue;
      const pKey = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
      const done = progressIndex[pKey] || 0;
      if (done > 0 && done < total) {
        incomplete.push({
          folderName: rec.folderName,
          lessonId: rec.lessonId,
          lastVisited: Number(rec.lastVisited) || 0,
        });
      }
    }

    if (incomplete.length === 0) return null;
    incomplete.sort((a, b) => b.lastVisited - a.lastVisited);
    return incomplete[0];
  } catch {
    return null;
  }
};









// ================================================================
// PROGRESS HUB
// ================================================================
const buildJourneyDots = (percent) => {
  if (percent >= 100) return Array(10).fill('done');
  const completed = Math.floor(percent / 10);
  return Array.from({ length: 10 }, (_, i) => {
    if (i < completed) return 'done';
    if (i === completed) return 'current';
    return 'empty';
  });
};

const ProgressHub = ({ courses, stats, navigate, userId }) => {
  const topCourses = courses.slice(0, 4);

  const courseActions = useMemo(() => {
    const actions = {};
    topCourses.forEach((c) => {
      if (!userId || userId === 'unknown' || c.percent === 0) {
        actions[c.key] = { type: 'start', target: null };
      } else {
        const lastIncomplete = getLastIncompleteLesson(userId, c.grade, c.subject);
        actions[c.key] = { type: 'continue', target: lastIncomplete };
      }
    });
    return actions;
  }, [userId, courses]);

  return (
    <div className="progress-hub">
      <div className="ph-header">
        <div className="ph-header-icon"><i className="fas fa-map" /></div>
        <div className="ph-header-title">سفر یادگیری من</div>
        <span className="ph-header-badge">
          {toPersianDigits(courses.length)} درس
        </span>
      </div>

      <div className="ph-journey">
        {topCourses.length === 0 ? (
          <div className="journey-empty">
            📚 هنوز درسی شروع نکردی — از کتاب‌ها یکی رو انتخاب کن
          </div>
        ) : (
          topCourses.map((c) => {
            const dots = buildJourneyDots(c.percent);
            const isDone = c.percent >= 100;
            const isFresh = c.percent === 0;
            const action = courseActions[c.key] || { type: 'start', target: null };

            const handleActionClick = (e) => {
              e.stopPropagation();
              if (action.type === 'start') {
                navigate(`/course/${c.grade}/${c.subject}`);
              } else if (action.target) {
                const path = [
                  '/Lessons-Gam',
                  encodeURIComponent(c.grade),
                  encodeURIComponent(c.subject),
                  encodeURIComponent(action.target.folderName),
                  encodeURIComponent(action.target.lessonId),
                ].join('/');
                navigate(path);
              } else {
                navigate(`/book/${c.grade}/${c.subject}`);
              }
            };

            return (
              <div
                key={c.key}
                className="journey-row"
                onClick={() => navigate(`/course/${c.grade}/${c.subject}`)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/course/${c.grade}/${c.subject}`);
                  }
                }}
              >
                <div className="journey-subject">
                  <span className="journey-emoji">{c.emoji}</span>
                  <span className="journey-name">{c.title}</span>
                </div>
                <div className="journey-dots">
                  {dots.map((s, i) => (
                    <div key={i} className={`journey-dot ${s === 'done' ? 'done' : s === 'current' ? 'current' : ''}`} />
                  ))}
                </div>
                <div className={`journey-percent ${isDone ? 'done' : isFresh ? 'fresh' : ''}`}>
                  {isFresh ? 'شروع کن' : `${toPersianDigits(c.percent)}٪`}
                </div>
                <button
                  type="button"
                  className={`journey-action-btn ${action.type}`}
                  onClick={handleActionClick}
                  title={action.type === 'start' ? 'شروع درس' : 'ادامه از جایی که بودی'}
                  aria-label={action.type === 'start' ? `شروع ${c.title}` : `ادامه ${c.title}`}
                >
                  <i className={`fas ${action.type === 'start' ? 'fa-play' : 'fa-arrow-left'}`} />
                </button>
              </div>
            );
          })
        )}
      </div>

      <div className="ph-stats">
        <div className="stat-box fire">
          <div className="stat-icon"><i className="fas fa-fire" /></div>
          <div className="stat-value">{toPersianDigits(stats.streak)}</div>
          <div className="stat-label">روز پیاپی</div>
        </div>
        <div className="stat-box time">
          <div className="stat-icon"><i className="fas fa-clock" /></div>
          <div className="stat-value">{toPersianDigits(stats.todayMinutes)}</div>
          <div className="stat-label">دقیقه امروز</div>
        </div>
        <div className="stat-box xp">
          <div className="stat-icon"><i className="fas fa-book" /></div>
          <div className="stat-value">{toPersianDigits(stats.booksCount)}</div>
          <div className="stat-label">کتاب‌های من</div>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// DISCOVERY
// ================================================================
const DragonCard = ({ challenge, navigate }) => {
  const { dragon, xp } = challenge;
  const [imgError, setImgError] = useState(false);

  const idx = getLeagueIdxByScore(xp || 0);
  const nextDragon = DRAGONS[idx + 1];
  const rangeStart = dragon.minScore;
  const rangeEnd = nextDragon ? nextDragon.minScore : null;
  const progress = rangeEnd
    ? Math.min(100, ((xp - rangeStart) / (rangeEnd - rangeStart)) * 100)
    : 100;
  const xpToNext = rangeEnd ? Math.max(0, rangeEnd - xp) : 0;

  const showImage = dragon.image && !imgError;

  return (
    <div
      className="discover-card discover-dragon"
      onClick={() => navigate('/medals')}
      role="button"
      tabIndex={0}
      aria-label={`لیگ فعلی: ${dragon.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/medals');
        }
      }}
      style={{
        '--dragon-glow': dragon.glow,
        '--dragon-c1': dragon.c1,
        '--dragon-c2': dragon.c2,
      }}
    >
      <div className="sd-pillar left" aria-hidden="true" />
      <div className="sd-pillar right" aria-hidden="true" />
      <div className="sd-bg-aura" aria-hidden="true" />
      <div className="sd-bg-stars" aria-hidden="true" />
      <div className="sd-bg-fog" aria-hidden="true" />
      <div className="sd-floor" aria-hidden="true" />
      <div className="sd-brazier left" aria-hidden="true">
        <div className="sd-brazier-flame" />
        <div className="sd-brazier-bowl" />
        <div className="sd-brazier-stand" />
      </div>
      <div className="sd-brazier right" aria-hidden="true">
        <div className="sd-brazier-flame" />
        <div className="sd-brazier-bowl" />
        <div className="sd-brazier-stand" />
      </div>

      <div className="discover-badge">🐉 لیگ فعلی</div>

      <div className="sd-dragon-stage">
        <div className="sd-spell-ring" aria-hidden="true" />
        <div className="sd-dragon-visual">
          {showImage ? (
            <img
              src={dragon.image}
              alt={dragon.name}
              className="sd-dragon-img"
              draggable={false}
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="sd-dragon-emoji">{dragon.emoji}</span>
          )}
        </div>
      </div>

      <div className="discover-title">لیگ {dragon.name}</div>
      

      <div className="discover-footer">
        <span className="dragon-special-mission-btn" aria-hidden="true">
          <i className="fas fa-star" />
          <span>ماموریت ویژه</span>
        </span>
      </div>
    </div>
  );
};

const MiniAiCard = ({ navigate, user }) => {
  const { exams } = useExams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) { setLoading(false); return; }
    (async () => {
      try {
        const result = await getDailyPlan(user.id, user.grade, user, exams);
        if (!cancelled) { setPlan(result); setLoading(false); }
      } catch (e) {
        console.warn('[MiniAiCard]', e);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.grade, exams, user]);

  const suggestion = plan?.suggestions?.[0];
  const previewText = loading
    ? 'در حال آماده‌سازی...'
    : (suggestion?.title || 'بر اساس کارنامه‌ات پیشنهاد آماده‌ست');

  return (
    <div
      className="discover-card discover-ai"
      onClick={() => navigate('/ai', { state: { autoCoach: true } })}
      title={previewText}
    >
      <img
        src="/Games/AI-fantasy.png"
        alt="مشاور هوشمند"
        className="challenge-image"
        draggable={false}
      />
      <div className="challenge-overlay" aria-hidden="true" />
      <div className="challenge-badge">
        <i className="fas fa-star" />
        <span>پیشنهاد ویژه امروز</span>
      </div>
      <div className="challenge-footer">
        <span className="challenge-title">مشاور هوشمند</span>
        <span className="challenge-start-btn" aria-hidden="true">
          <i className="fas fa-bolt" />
          <span>عجله کن!</span>
        </span>
      </div>
    </div>
  );
};

const ChallengeCard = ({ challenge, navigate }) => {
  const count = challenge?.activeChallenges || 0;
  const badgeText = count > 0
    ? `${toPersianDigits(count)} چالش`
    : 'چالش';

  return (
    <div
      className="discover-card discover-challenge"
      onClick={() => navigate('/Challenge')}
    >
      <img
        src="/Games/challenge-fantasy.png"
        alt="میدان چالش"
        className="challenge-image"
        draggable={false}
      />
      <div className="challenge-overlay" aria-hidden="true" />
      <div className="challenge-badge">
        <i className="fas fa-bolt" />
        <span>{badgeText}</span>
      </div>
      <div className="challenge-footer">
        <span className="challenge-title">میدان چالش</span>
        <span className="challenge-start-btn" aria-hidden="true">
          <i className="fas fa-play" />
          <span>شروع</span>
        </span>
      </div>
    </div>
  );
};

const DiscoveryScroll = ({ user, exams, navigate }) => {
  const challenge = useChallengeStats(user?.id, user?.grade);

  return (
    <div className="discover-scroll">
      <DragonCard challenge={challenge} navigate={navigate} />
      <MiniAiCard navigate={navigate} user={user} />
      <ChallengeCard challenge={challenge} user={user} exams={exams} navigate={navigate} />
    </div>
  );
};

// ================================================================
// GAMES MARQUEE
// ================================================================
const MARQUEE_GAMES = [
  { id: 1, title: 'سودوکو', image: '/Games/Game7-1.png', url: 'http://hamrahgameland.ir/Games/Sudoku/', players: 234, rating: 4.8 },
  { id: 2, title: 'پنالتی', image: '/Games/Game1-3.png', url: 'http://hamrahgameland.ir/Games/penalty-shooter/', players: 567, rating: 4.9 },
  { id: 3, title: 'خانه ساز', image: '/Games/Game1-4.png', url: 'http://tower.shadnoyan.com/', players: 678, rating: 4.9 },
  { id: 4, title: 'هوش برتر', image: '/Games/Game3-1.png', url: 'http://hamrahgameland.ir/Games/HoosheBartar/', players: 312, rating: 4.9 },
  { id: 5, title: 'فوتبال جاذبه', image: '/Games/Game4-3.png', url: 'http://hamrahgameland.ir/Games/FootBallJazebe/', players: 412, rating: 4.8 },
  { id: 6, title: '۲۰۴۸', image: '/Games/Game5-1.png', url: 'http://hamrahgameland.ir/Games/2048/', players: 423, rating: 4.7 },
  { id: 7, title: 'سرخابی', image: '/Games/Game2-3.png', url: 'http://hamrahgameland.ir/Games/sorkhabi/', players: 345, rating: 4.7 },
  { id: 8, title: 'توپ خرابکار', image: '/Games/Game1-2.png', url: 'http://hamrahgameland.ir/Games/ToopeKharabKar/', players: 267, rating: 4.5 },
  { id: 9, title: 'توپ آبی', image: '/Games/Game2-4.png', url: 'http://hamrahgameland.ir/Games/ToopAbi/', players: 456, rating: 4.6 },
  { id: 10, title: 'بیگانگان', image: '/Games/Game8-1.png', url: 'http://hamrahgameland.ir/Games/biganegan/', players: 234, rating: 4.5 },
  { id: 11, title: 'مرتب کردن توپ‌ها', image: '/Games/Game2-1.png', url: 'http://hamrahgameland.ir/Games/MoratabToop/', players: 187, rating: 4.6 },
  { id: 12, title: 'بازی حافظه', image: '/Games/Game6-1.png', url: 'http://hamrahgameland.ir/Games/bazihafeze/', players: 198, rating: 4.4 },
  { id: 13, title: 'کامیون رنگ', image: '/Games/Game4-2.png', url: 'http://hamrahgameland.ir/Games/KamionRang/', players: 234, rating: 4.3 },
  { id: 14, title: 'نجات فضانورد', image: '/Games/Game3-4.png', url: 'http://hamrahgameland.ir/Games/FazaNavard/', players: 234, rating: 4.5 },
];

const MarqueeGameCard = ({ game }) => {
  const openGame = () => {
    if (game.url) window.open(game.url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="gm-card" onClick={openGame}>
      <div className="gm-card-img" style={{ backgroundImage: `url(${game.image})` }} />
      <span className="gm-card-badge">⭐ {game.rating}</span>
      <div className="gm-card-play">▶</div>
      <div className="gm-card-body">
        <h4 className="gm-card-title">{game.title}</h4>
        <div className="gm-card-meta">
          <span>👥 {game.players}</span>
        </div>
      </div>
    </div>
  );
};

const GamesMarquee = ({ navigate }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const groupARef = useRef(null);
  const stateRef = useRef({ offset: 0, loopWidth: 0, lastTime: 0, paused: false });
  const visibleRef = useRef(false);
  const rafRef = useRef(null);
  const tickRef = useRef(null);

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const groupA = groupARef.current;
    if (!viewport || !track || !groupA) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 10;
    const groupWidth = groupA.getBoundingClientRect().width;
    stateRef.current.loopWidth = groupWidth + gap;
    stateRef.current.offset = -(Math.abs(stateRef.current.offset) % stateRef.current.loopWidth);
    track.style.transform = `translate3d(${stateRef.current.offset}px,0,0)`;
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (viewportRef.current) ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;
        if (!entry.isIntersecting && rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        } else if (entry.isIntersecting && rafRef.current == null && !document.hidden) {
          stateRef.current.lastTime = 0;
          rafRef.current = requestAnimationFrame(tickRef.current);
        }
      },
      { threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const SPEED = 42;
    const tick = (now) => {
      const s = stateRef.current;
      if (!s.lastTime) s.lastTime = now;
      let dt = (now - s.lastTime) / 1500;
      s.lastTime = now;
      if (dt > 0.1) dt = 0.1;
      if (!s.paused && s.loopWidth > 0 && trackRef.current) {
        s.offset -= SPEED * dt;
        if (s.offset <= -s.loopWidth) s.offset += s.loopWidth;
        if (s.offset > 0) s.offset -= s.loopWidth;
        trackRef.current.style.transform = `translate3d(${s.offset}px,0,0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tickRef.current = tick;

    const start = () => {
      if (rafRef.current == null && visibleRef.current && !document.hidden) {
        stateRef.current.lastTime = 0;
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const pause = () => { stateRef.current.paused = true; stateRef.current.lastTime = 0; };
  const play = () => { stateRef.current.paused = false; };

  return (
    <div className="games-card">
      <div className="gm-head">
        <div className="gm-title">
          <div className="gm-title-icon"><i className="fas fa-gamepad" /></div>
          <span>بازی‌های آنلاین</span>
        </div>
        <span className="gm-live">LIVE</span>
      </div>

      <div
        className="gm-viewport"
        ref={viewportRef}
        onMouseEnter={pause}
        onMouseLeave={play}
      >
        <div className="gm-track" ref={trackRef}>
          <div className="gm-group" ref={groupARef}>
            {MARQUEE_GAMES.map((game, i) => (
              <MarqueeGameCard key={`a-${i}`} game={game} />
            ))}
          </div>
          <div className="gm-group">
            {MARQUEE_GAMES.map((game, i) => (
              <MarqueeGameCard key={`b-${i}`} game={game} />
            ))}
          </div>
        </div>
      </div>

      <button className="gm-cta" onClick={() => navigate('/games')} type="button">
        <i className="fas fa-arrow-left" />
        مشاهده همه بازی‌ها
      </button>
    </div>
  );
};

// ================================================================
// FOOTER — Parent Compact + Fact
// ================================================================
const ParentCard = ({ navigate }) => {
  const { user } = useAuth();
  const {
    performance, exams: examAvg, study, total, details,
  } = useParentStats(user?.id, user?.grade);

  const perfTip = `عملکرد: پوشش ${details.performance.coverage}٪ × ۵۰٪ + عمق ${details.performance.depth}٪ × ۵۰٪`;
  const examTip = details.exams.count > 0
    ? `میانگین وزنی ${details.exams.count} آزمون (جدیدترها وزن بیشتر)`
    : 'هنوز آزمون تصحیح‌شده‌ای نداری';
  const studyTip =
    `این هفته: ${details.study.minutes} دقیقه از هدف ${details.study.goal} دقیقه\n` +
    `اتاق مطالعه: ${details.study.breakdown.studyTimer || 0} دقیقه • برنامه‌ریزی: ${details.study.breakdown.lessonPlanning || 0} دقیقه`;

return (
  <div
    className="parent-card"
    onClick={() => navigate('/parent-dashboard')}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        navigate('/parent-dashboard');
      }
    }}
  >
      <div className="parent-head">
        {/*<div className="parent-avatar">
          <i className="fas fa-user-friends"></i>
        </div>*/}
        <div className="parent-head-text">
          <span className="parent-title">داشبورد والدین</span>
          <span className="parent-sub">گزارش کامل در دسترس</span>
        </div>
      </div>

      <div className="parent-rings">
        <div className="parent-rings-visual">
          <svg className="parent-rings-svg" viewBox="0 0 130 130">
            <defs>
              <linearGradient
                id="parentGrad1"
                gradientUnits="userSpaceOnUse"
                x1="10" y1="10" x2="120" y2="120"
              >
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="100%" stopColor="#7c3aed" />
              </linearGradient>
              <linearGradient
                id="parentGrad2"
                gradientUnits="userSpaceOnUse"
                x1="20" y1="20" x2="110" y2="110"
              >
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
              <linearGradient
                id="parentGrad3"
                gradientUnits="userSpaceOnUse"
                x1="35" y1="35" x2="95" y2="95"
              >
                <stop offset="0%" stopColor="#5eead4" />
                <stop offset="100%" stopColor="#18b9ab" />
              </linearGradient>
            </defs>

            {/* حلقه ۱ */}
            <g className="parent-ring-1">
              <circle className="parent-ring-track" cx="65" cy="65" r="58" fill="none" />
              <circle
                className="parent-ring-progress"
                cx="65" cy="65" r="58"
                fill="none"
                stroke="url(#parentGrad1)"
                strokeDasharray={2 * Math.PI * 58}
                strokeDashoffset={2 * Math.PI * 58 * (1 - Math.min(100, Math.max(0, performance)) / 100)}
              />
            </g>

            {/* حلقه ۲ */}
            <g className="parent-ring-2">
              <circle className="parent-ring-track" cx="65" cy="65" r="43" fill="none" />
              <circle
                className="parent-ring-progress"
                cx="65" cy="65" r="43"
                fill="none"
                stroke="url(#parentGrad2)"
                strokeDasharray={2 * Math.PI * 43}
                strokeDashoffset={2 * Math.PI * 43 * (1 - Math.min(100, Math.max(0, examAvg)) / 100)}
              />
            </g>

            {/* حلقه ۳ */}
            <g className="parent-ring-3">
              <circle className="parent-ring-track" cx="65" cy="65" r="28" fill="none" />
              <circle
                className="parent-ring-progress"
                cx="65" cy="65" r="28"
                fill="none"
                stroke="url(#parentGrad3)"
                strokeDasharray={2 * Math.PI * 28}
                strokeDashoffset={2 * Math.PI * 28 * (1 - Math.min(100, Math.max(0, study)) / 100)}
              />
            </g>
          </svg>

          <div
            className="parent-rings-center"
            title={`میانگین سه معیار:\nعملکرد ${performance}٪ • آزمون ${examAvg}٪ • مطالعه ${study}٪`}
          >
            <span className="parent-rings-value">
              {toPersianDigits(Math.round((performance + examAvg + study) / 3))}٪
            </span>
            <span className="parent-rings-caption">امتیاز کل</span>
          </div>
        </div>

        <div className="parent-legend">
          <div className="parent-legend-item" title={perfTip}>
            <span className="parent-legend-dot d1"></span>
            <div className="parent-legend-text">
              <span className="parent-legend-title">عملکرد</span>
              <span className="parent-legend-value">{toPersianDigits(performance)}٪</span>
            </div>
          </div>
          <div className="parent-legend-item" title={examTip}>
            <span className="parent-legend-dot d2"></span>
            <div className="parent-legend-text">
              <span className="parent-legend-title">آزمون‌ها</span>
              <span className="parent-legend-value">{toPersianDigits(examAvg)}٪</span>
            </div>
          </div>
          <div className="parent-legend-item" title={studyTip}>
            <span className="parent-legend-dot d3"></span>
            <div className="parent-legend-text">
              <span className="parent-legend-title">مطالعه</span>
              <span className="parent-legend-value">{toPersianDigits(study)}٪</span>
            </div>
          </div>
        </div>
      </div>

      {/*<button
        className="parent-cta"
        onClick={() => navigate('/parent-dashboard')}
        type="button"
      >
        <i className="fas fa-arrow-left"></i>
        مشاهده داشبورد کامل
      </button>*/}
     
    </div>
  );
};

const DAILY_FACTS = [
  'ضرب عدد ۹ رو می‌تونی با انگشت‌هات حساب کنی! کافیه انگشت نهم رو خم کنی.',
  'هر روز فقط ۱۵ دقیقه مطالعه = ۹۱ ساعت در سال!',
  'قبل از خواب مطالب حفظی رو مرور کن — مغزت شب کار می‌کنه!',
  'اشتباه کردن بخش مهمی از یادگیری‌ست، نترس!',
  'بهترین زمان یادگیری وقتی‌ست که خسته نیستی.',
  'وقتی یه چیز جدید یاد می‌گیری، مغزت یه قدم بزرگ‌تر می‌شه!',
  'سوال پرسیدن نشانه‌ی باهوش بودنه، نه نادانی!',
  'یه نفس عمیق بکش، بعد ادامه بده!',
  'آب خوردن موقع مطالعه به تمرکزت کمک می‌کنه!',
  'نوشتن با دست، یادگیری رو قوی‌تر می‌کنه!',
];

const FactCard = () => {
  const fact = useMemo(() => {
    const day = new Date().getDate();
    return DAILY_FACTS[day % DAILY_FACTS.length];
  }, []);

  {/*return (
    <div className="fact-card">
      <div className="fact-head">
        <div className="fact-icon"><i className="fas fa-lightbulb" /></div>
        <div className="fact-title">می‌دونستی؟</div>
      </div>
      <div className="fact-text">{fact}</div>
    </div>
  );*/}
};

// ================================================================
// ZONE DIVIDER
// ================================================================
const ZoneDivider = ({ children }) => (
  <div className="zone-divider">
    <span className="zone-text">{children}</span>
  </div>
);

// ================================================================
// 🆕 TEACHER STORY SHELF — کپسول اساتید برگزیده
// ================================================================
// داده از سرور: GET /api/db/teacherShelfConfigs/{grade}-{field}
// اگر config نبود یا خالی بود → return null (shelf مخفی)
// ================================================================
const TeacherStoryShelf = ({ user, navigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const userGrade = normalizeGradeId(user?.grade);
      if (!userGrade) {
        if (!cancelled) { setItems([]); setLoading(false); }
        return;
      }

      const userField = user?.field ? String(user.field) : null;
      const key = `${userGrade}-${userField || 'none'}`;

      try {
        const token = getToken();
        const res = await fetch(`/api/db/teacherShelfConfigs/${key}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          if (!cancelled) { setItems([]); setLoading(false); }
          return;
        }

        const config = await res.json();
        const configItems = Array.isArray(config?.items) ? config.items : [];
        if (configItems.length === 0) {
          if (!cancelled) { setItems([]); setLoading(false); }
          return;
        }

        // مرحله ۱ — resolve پایه‌ای (بدون blob)
        const resolvedBasic = configItems
          .slice()
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
          .map((item) => {
            try {
              const teacher = dataEngine.teacher.get(item.teacherId);
              if (!teacher || teacher.archivedAt || teacher.isActive === false) return null;
              const project = dataEngine.project.get(item.projectId);
              if (!project || !project.publishedVersionId) return null;

              return {
                id: item.id || `itm-${item.teacherId}-${item.subject}`,
                teacherId: teacher.id,
                projectId: project.id,
                folderName: teacher.folderName || '',
                name: item.displayName || teacher.fullName || 'معلم',
                subject: item.displaySubject || SUBJECT_LABELS[item.subject] || item.subject,
                subjectId: item.subject,
                field: item.field || null,
                image: teacher.legacyAvatar || null,
                avatarBlobId: teacher.avatarBlobId || null,
                isSpecial:
                  item.isSpecial !== null && item.isSpecial !== undefined
                    ? !!item.isSpecial
                    : !!(teacher.legacyIsSpecial || project.stats?.isSpecial),
                stars: project.stats?.avgRating || teacher.legacyStars || 0,
              };
            } catch {
              return null;
            }
          })
          .filter(Boolean);

        // مرحله ۲ — resolve blob → URL
        const resolved = await Promise.all(
          resolvedBasic.map(async (r) => {
            let image = r.image;
            if (!image && r.avatarBlobId) {
              try { image = await engine.blobs.getUrl(r.avatarBlobId); } catch {}
            }
            return { ...r, image };
          })
        );

        if (!cancelled) {
          setItems(resolved);
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setItems([]); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.grade, user?.field]);

  if (loading) return null;
  if (items.length === 0) return null;

  const SUBJECT_RING_MAP = {
    riazi: 'math', olum: 'science', farsi: 'farsi', ghoran: 'quran',
    motaleat: 'social', hedye: 'gift', tafakor: 'think', kar: 'kar',
    arabi: 'arabi', english: 'english', din: 'din', fizik: 'fizik',
    shimi: 'shimi', zist: 'zist',
  };

  const handleClick = (item) => {
    const teacherState = {
      id: item.teacherId,
      name: item.name,
      folderName: item.folderName,
      image: item.image,
      avatarBlobId: item.avatarBlobId,
      isSpecial: item.isSpecial,
      stars: item.stars,
    };
    navigate(`/book/${user.grade}/${item.subjectId}`, {
      state: {
        field: item.field || user.field || undefined,
        teacher: teacherState,
      },
    });
  };

  return (
    <div className="tsh-shelf">
      <div className="tsh-head">
        <div className="tsh-head-left">
          <span className="tsh-head-icon">✨</span>
          <span className="tsh-head-title">اساتید <em>برگزیده</em></span>
        </div>
      </div>
      <div className="tsh-viewport">
        {items.map((item, i) => {
          const rank = i + 1;
          const isRanked = rank <= 3;
          const ringKey = isRanked
            ? `rank-${rank}`
            : (SUBJECT_RING_MAP[item.subjectId] || 'math');
          return (
            <button
              key={item.id}
              type="button"
              className={`tsh-card ${isRanked ? 'is-ranked' : ''}`}
              onClick={() => handleClick(item)}
            >
              <div className={`tsh-ring tsh-rg-${ringKey}`}>
                <div className="tsh-inner">
                  {item.image ? (
                    <img className="tsh-avatar" src={item.image} alt={item.name} loading="lazy" />
                  ) : (
                    <div className="tsh-avatar-fallback">
                      {(item.name || 'م').charAt(0)}
                    </div>
                  )}
                </div>
                {isRanked && (
                  <span className={`tsh-rank tsh-rank-${rank}`}>
                    {rank.toLocaleString('fa-IR')}
                  </span>
                )}
                {item.isSpecial && <span className="tsh-crown">👑</span>}
              </div>
              <div className="tsh-name">{item.name}</div>
              <div className="tsh-meta">
                <span className="tsh-subject">{item.subject}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ================================================================
// STUDENT DASHBOARD — content layout
// ================================================================
const StudentDashboard = () => {

  const navigate = useNavigate();
  const { user } = useAuth();
  const { exams } = useExams();
  const { xp } = useCoins();

  const courses = useMyCourses(user?.id, user?.grade);
  const studyStats = useStudyStats(user?.id);
  const level = Math.floor((xp || 0) / 100) + 1;

  return (
    <div className="app">
      <div className="top-band">
      <HUD user={user} streak={studyStats.streak} level={level} />
      </div>
      <Hero navigate={navigate} user={user} />

      <TeacherStoryShelf user={user} navigate={navigate} />

      <QuickAccess navigate={navigate} />

      <ProgressHub courses={courses} stats={studyStats} navigate={navigate} userId={user?.id} />

      <ZoneDivider>✨ دنیای ماجراجویی ✨</ZoneDivider>

      <DiscoveryScroll user={user} exams={exams} navigate={navigate} />

      <ZoneDivider>📊 پیشرفت تحصیلی 📈</ZoneDivider>

      <WeeklyProgressCard />



      <ZoneDivider>🎮 وقت بازیه! 🎯</ZoneDivider>

      <GamesMarquee navigate={navigate} />

<FactCard />
    </div>
  );
};

// ================================================================
// STUDENT HOME — wrapper with theme
// ================================================================
const StudentHome = () => {
  const [isDark, setIsDark] = useState(() => {
    try {
      return engine.getRaw('navbar2-drawer-dark') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      engine.setRaw('navbar2-drawer-dark', isDark ? '1' : '0');
    } catch { /* ignore */ }
  }, [isDark]);

  useEffect(() => {
    const readTheme = () => {
      try {
        const current = engine.getRaw('navbar2-drawer-dark') === '1';
        setIsDark((prev) => (prev !== current ? current : prev));
      } catch { /* ignore */ }
    };
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') readTheme();
    };
    const handleVisibility = () => {
      if (!document.hidden) readTheme();
    };
    const handleThemeEvent = () => readTheme();

    window.addEventListener('storage', handleStorage);
    window.addEventListener('theme-change', handleThemeEvent);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('theme-change', handleThemeEvent);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <div className={`student-home ${isDark ? 'dark-mode' : ''}`}>
      <StudentDashboard />
    </div>
  );
};

export default StudentHome;